import React from 'react';
import { Shield, Clock, Users, Award, CheckCircle2 } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: 'Esperienza Certificata',
    description: 'Professionisti qualificati con certificazioni nel settore energetico e finanziario.',
    stat: '10+',
    statLabel: 'Anni di esperienza'
  },
  {
    icon: Clock,
    title: 'Risultati Rapidi',
    description: 'Tempi di analisi e implementazione ridotti grazie a processi ottimizzati.',
    stat: '30',
    statLabel: 'Giorni medi'
  },
  {
    icon: Users,
    title: 'Approccio Personalizzato',
    description: 'Ogni azienda è unica. Sviluppiamo soluzioni su misura per le tue esigenze.',
    stat: '100%',
    statLabel: 'Dedizione al cliente'
  },
  {
    icon: Award,
    title: 'Track Record Solido',
    description: 'Risultati misurabili e documentati per ogni progetto realizzato.',
    stat: '€5M+',
    statLabel: 'Risparmi generati'
  }
];

const benefits = [
  'Consulenza iniziale gratuita e senza impegno',
  'Supporto dedicato durante tutto il processo',
  'Monitoraggio continuo dei risultati',
  'Aggiornamento costante su nuovi incentivi',
  'Rete di partner tecnologici qualificati'
];

const WhyUs = () => {
  return (
    <section id="perche-noi" data-testid="why-us-section" className="py-24 md:py-32 relative bg-[#0a0f1a]">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F5A547]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#C14B7E]/5 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-left mb-16 md:mb-20">
          <span className="text-sm uppercase tracking-widest font-bold text-[#F5A547] mb-4 block">
            Perché Scegliere DODIPA
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            I Migliori Partner per la
            <br />
            <span className="gradient-text">Tua Transizione Energetica</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl">
            Affidati a chi conosce il settore e può guidarti verso risultati concreti.
            La nostra missione è trasformare l'energia in opportunità per la tua azienda.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="glass-card rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-300"
              data-testid={`why-us-card-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#5CBDB0]/10 flex items-center justify-center mb-5">
                <feature.icon className="w-6 h-6 text-[#5CBDB0]" />
              </div>
              
              <div className="mb-4">
                <div className="font-heading text-3xl font-bold text-[#F5A547]">{feature.stat}</div>
                <div className="text-white/40 text-xs uppercase tracking-wider">{feature.statLabel}</div>
              </div>

              <h3 className="font-heading text-lg font-semibold text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-8">
              Cosa Ottieni Lavorando con Noi
            </h3>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <li 
                  key={index} 
                  className="flex items-start gap-3"
                  data-testid={`benefit-item-${index}`}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#5CBDB0] flex-shrink-0 mt-0.5" />
                  <span className="text-white/70">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Card */}
          <div className="glass-card rounded-3xl p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#5CBDB0]/10 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-xl bg-[#5CBDB0]/20 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-[#5CBDB0]" />
              </div>
              
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">
                La Tua Energia, La Nostra Missione
              </h3>
              
              <p className="text-white/70 text-base leading-relaxed mb-6">
                Analizziamo ogni aspetto della tua fornitura energetica: dalle tariffe ai codici ATECO, 
                dal recupero crediti alle opportunità di finanziamento. 
                Un partner unico per tutte le tue esigenze energetiche.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-lg bg-white/5">
                  <div className="font-heading text-2xl font-bold text-[#5CBDB0]">100%</div>
                  <div className="text-white/40 text-xs">Trasparenza</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white/5">
                  <div className="font-heading text-2xl font-bold text-[#F5A547]">360°</div>
                  <div className="text-white/40 text-xs">Assistenza</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
